import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/hooks/useContent";
import { useProfile, useUserState, useSetReminder } from "@/hooks/useUserData";
import { ProfileBars } from "@/components/ProfileBars";
import { PillarIcon } from "@/components/PillarIcon";
import { AddToHomeCard } from "@/components/AddToHomeCard";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RefreshCw, LogOut, Mail, Bell, Instagram } from "lucide-react";

// Handle Instagram ufficiale di Be Woman
const INSTAGRAM_URL = "https://www.instagram.com/bewoman_academy";

function InstagramCard() {
  return (
    <a
      href={INSTAGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="profilo-instagram"
      className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4 flex items-center gap-4"
    >
      <span
        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 45%,#8134AF 100%)" }}
      >
        <Instagram className="w-6 h-6 text-white" />
      </span>
      <div className="flex-1">
        <p className="font-semibold text-[#F6DCEA] leading-tight">Seguici su Instagram</p>
        <p className="text-sm text-[#EAC6D9]">Unisciti alla community Be Woman: ispirazione ogni giorno 💗</p>
      </div>
    </a>
  );
}

function ReminderCard() {
  const { data: state } = useUserState();
  const setReminder = useSetReminder();
  const reminder = state?.reminder || { enabled: false, time: "20:00" };
  const [time, setTime] = useState(reminder.time || "20:00");

  useEffect(() => {
    if (reminder.time) setTime(reminder.time);
  }, [reminder.time]);

  const onToggle = async (enabled) => {
    // Persist the preference FIRST (source of truth), regardless of notification permission.
    try {
      await setReminder.mutateAsync({ enabled, time });
      toast.success(enabled ? "Promemoria attivato" : "Promemoria disattivato");
    } catch {
      toast.error("Errore nel salvataggio");
      return;
    }
    // Then request browser notification permission (best-effort, non-blocking).
    if (enabled && typeof Notification !== "undefined" && Notification.permission === "default") {
      try {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          toast.info("Attiva le notifiche del browser per ricevere il promemoria");
        }
      } catch {
        /* ignore permission errors */
      }
    }
  };

  const onTimeChange = async (newTime) => {
    setTime(newTime);
    if (reminder.enabled) {
      try { await setReminder.mutateAsync({ enabled: true, time: newTime }); } catch {}
    }
  };

  return (
    <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4" data-testid="profilo-reminder-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#E32C8C]/10 flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5 text-[#E32C8C]" />
          </span>
          <div>
            <p className="font-semibold text-[#F6DCEA] leading-tight">Promemoria quotidiano</p>
            <p className="text-xs text-[#EAC6D9]">Un invito gentile al tuo gesto di oggi</p>
          </div>
        </div>
        <Switch
          checked={reminder.enabled}
          onCheckedChange={onToggle}
          data-testid="reminder-switch"
          className="data-[state=checked]:bg-[#E32C8C]"
        />
      </div>
      {reminder.enabled && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#6B2247]">
          <span className="text-sm text-[#EAC6D9]">A che ora?</span>
          <input
            type="time"
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
            data-testid="reminder-time-input"
            className="bg-[#00000000] border border-[#E32C8C]/35 bw-card rounded-xl px-3 py-2 text-[#F6DCEA] font-semibold focus:outline-none focus:border-[#E32C8C]"
          />
        </div>
      )}
    </div>
  );
}

export function Profilo() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: content } = useContent();
  const { data: profile } = useProfile();

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const firstName = (user?.name || "").split(" ")[0] || "";
  const initial = firstName ? firstName[0].toUpperCase() : "B";

  return (
    <div className="bw-fade">
      <div className="mb-5">
        <p className="text-xs uppercase tracking-widest text-[#EAC6D9] font-semibold">Il tuo profilo</p>
        <h1 className="font-serif text-4xl font-bold text-[#F6DCEA]">Ciao {firstName || "bellezza"}</h1>
      </div>

      {/* User card */}
      <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4 flex items-center gap-4" data-testid="profilo-user-card">
        {user?.picture ? (
          <img src={user.picture} alt={firstName} className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <span className="w-16 h-16 rounded-full flex items-center justify-center text-white font-serif text-3xl font-bold" style={{ background: "linear-gradient(135deg,#E32C8C,#8E1A52)" }}>
            {initial}
          </span>
        )}
        <div className="min-w-0">
          <p className="font-serif text-2xl font-semibold text-[#F6DCEA] leading-tight truncate">{user?.name}</p>
          <p className="text-sm text-[#EAC6D9] flex items-center gap-1.5 truncate"><Mail className="w-3.5 h-3.5" /> {user?.email}</p>
        </div>
      </div>

      {/* Profile bars */}
      {content && profile?.has_test && (
        <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4" data-testid="profilo-bars-card">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${content.pillars[profile.priority].color}1A` }}>
              <PillarIcon pillar={profile.priority} className="w-4 h-4" style={{ color: content.pillars[profile.priority].color }} />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#EAC6D9] font-semibold">Archetipo</p>
              <p className="font-serif text-xl font-semibold text-[#F6DCEA] leading-none">{profile.archetype.name}</p>
            </div>
          </div>
          <ProfileBars profile={profile} pillars={content.pillars} pillarOrder={content.pillar_order} compact />
        </div>
      )}

      {/* Reminder settings */}
      <ReminderCard />

      {/* Add to Home Screen (PWA) */}
      <AddToHomeCard />

      {/* Instagram follow */}
      <InstagramCard />

      {/* Actions */}
      <button
        onClick={() => navigate("/test")}
        data-testid="profilo-retake-test-btn"
        className="w-full bg-[#2A0A1B] text-[#F6DCEA] border border-[#E32C8C]/30 font-semibold py-3.5 px-6 rounded-full hover:bg-[#401431] transition-all flex items-center justify-center gap-2 mb-3"
      >
        <RefreshCw className="w-4 h-4" /> Rifai il test
      </button>
      <button
        onClick={doLogout}
        data-testid="profilo-logout-btn"
        className="w-full text-[#F04B9B] font-semibold py-3.5 px-6 rounded-full hover:bg-[#2A0A1B] transition-all flex items-center justify-center gap-2"
      >
        <LogOut className="w-4 h-4" /> Esci
      </button>
    </div>
  );
}
